<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { Guest } from '@/models'

const props = defineProps<{ guest: Guest | null }>()

const emit = defineEmits<{
  save: [payload: Partial<Guest>]
}>()

const visible = defineModel<boolean>('visible')

const formRef = ref()
const form = ref<Partial<Guest>>({
  company_id: '',
  primary_name: '',
  contact_number: '',
  email: '',
  adults: 1,
  children: 0,
  guest_names: [],
  notes: '',
})

function getCompanyId(): string {
  const raw = localStorage.getItem('user')
  if (!raw) return ''
  try {
    const u = JSON.parse(raw)
    return u?.company_id || ''
  } catch {
    return ''
  }
}

watch(visible, (newVal) => {
  if (newVal) {
    const val = props.guest
    if (val) {
      form.value = {
        company_id: val.company_id,
        primary_name: val.primary_name,
        contact_number: val.contact_number || '',
        email: val.email || '',
        adults: val.adults,
        children: val.children,
        guest_names: [...(val.guest_names || [])],
        notes: val.notes || '',
      }
    } else {
      form.value = {
        company_id: getCompanyId(),
        primary_name: '',
        contact_number: '',
        email: '',
        adults: 1,
        children: 0,
        guest_names: [],
        notes: '',
      }
    }
    formRef.value?.clearValidate?.()
  }
})

const rules = {
  primary_name: [{ required: true, message: 'Please enter guest name' }],
  contact_number: [
    {
      validator: (_rule: any, value: string) => {
        if (!value) return Promise.resolve()
        if (value.trim().length < 7) return Promise.reject(new Error('Contact number too short'))
        return Promise.resolve()
      },
    },
  ],
  email: [{ type: 'email' as const, message: 'Please enter a valid email' }],
  adults: [
    { required: true, message: 'Adults is required' },
    { type: 'number' as const, min: 1, message: 'At least 1 adult required' },
  ],
}

const addGuestName = () => {
  form.value.guest_names = [...(form.value.guest_names || []), '']
}

const removeGuestName = (idx: number) => {
  form.value.guest_names = (form.value.guest_names || []).filter((_, i) => i !== idx)
}

const handleSubmit = async () => {
  await formRef.value?.validate()
  emit('save', {
    ...form.value,
    guest_names: (form.value.guest_names || []).filter((n) => n.trim()),
  })
}

const handleClose = () => {
  visible.value = false
  formRef.value?.resetFields()
}
</script>

<template>
  <a-modal
    :open="visible"
    :title="guest ? 'Edit Guest' : 'New Guest'"
    @ok="handleSubmit"
    @cancel="handleClose"
    ok-text="Save"
    :width="560"
  >
    <a-form ref="formRef" :model="form" :rules="rules" layout="vertical">
      <a-form-item label="Primary Name" name="primary_name">
        <a-input v-model:value="form.primary_name" placeholder="Guest name" />
      </a-form-item>

      <a-row :gutter="12">
        <a-col :span="12">
          <a-form-item label="Contact Number" name="contact_number">
            <a-input v-model:value="form.contact_number" placeholder="Phone number" />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item label="Email" name="email">
            <a-input v-model:value="form.email" placeholder="guest@email.com" />
          </a-form-item>
        </a-col>
      </a-row>

      <a-row :gutter="12">
        <a-col :span="12">
           <a-form-item label="Adults" name="adults">
             <a-input-number v-model:value="form.adults" :min="1" :max="100" style="width: 100%" />
           </a-form-item>
         </a-col>
        <a-col :span="12">
          <a-form-item label="Children" name="children">
            <a-input-number v-model:value="form.children" :min="0" :max="100" style="width: 100%" />
          </a-form-item>
        </a-col>
      </a-row>

      <a-form-item label="Additional Guest Names">
        <div v-for="(_, idx) in form.guest_names" :key="idx" style="display: flex; gap: 8px; margin-bottom: 8px">
          <a-input
            v-model:value="form.guest_names![idx]"
            placeholder="Guest name"
            style="flex: 1"
          />
          <a-button @click="removeGuestName(idx)" danger size="small">Remove</a-button>
        </div>
        <a-button type="dashed" @click="addGuestName" block>
          + Add Guest Name
        </a-button>
      </a-form-item>

      <a-form-item label="Notes" name="notes">
        <a-textarea v-model:value="form.notes" :rows="3" placeholder="Optional notes" />
      </a-form-item>
    </a-form>
  </a-modal>
</template>
